import React, { useEffect, useMemo } from 'react';
import Immutable from 'immutable';
import { useSelector, useDispatch } from 'react-redux';
import {
  Node,
  Taxonomy as TaxonomyTerm,
  entityGetByPath,
  referenceLoadingReset,
} from '@uncinc/react-drupal-core';

import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';

import Page from '../Page';
import NotFound from '../NotFound';
import Forbidden from '../Forbidden';
import ServerError from '../ServerError';
import { usePrevious } from '../../helpers/usePrevious';
import { WebformWrapper } from '../../components';
import { clearPageInformation, setPageInformation, setPageViewSent } from '../../store/actions/UI';
import { ENTITY_TYPES } from '../../config/constants';

const Entity = () => {
  const dispatch = useDispatch();
  const path = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const node = useSelector((state) => state.getIn(['node', entityId], null));
  const taxonomyTerm = useSelector((state) => state.getIn(['taxonomyTerm', entityId], null));
  const webform = useSelector((state) => state.getIn(['webform', entityId], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Immutable.Map()));
  const entityStatus = useSelector((state) => state.getIn(['entity', 'status']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const prevPath = usePrevious(path);
  const entityNotLoaded = !entityType || !entityId;
  const pathIsDifferentAndHasEntity = (path !== prevPath && typeof prevPath !== 'undefined') && (typeof entityStatus !== 'undefined' && entityStatus !== null);

  // Build node metadata in format { theme, subjects, workfields }
  const buildNodeMetadata = (sourceNode) => {
    const metadata = {};

    const subjects = sourceNode.getIn(['field_subject'], null);
    const workfields = sourceNode.getIn(['field_workfield'], null);

    const mappedWorkfields = [];
    const mappedSubjects = [];

    if (subjects?.size) {
      subjects.forEach((subject) => {
        const name = subject.getIn(['entity', 'name', 0, 'value']);
        if (name) {
          mappedSubjects.push(name);
        }
      });
    }

    if (mappedSubjects.length > 0) {
      metadata.subjects = mappedSubjects;
    }

    if (workfields?.size) {
      workfields.forEach((workfield) => {
        const name = workfield.getIn(['entity', 'name', 0, 'value']);
        if (name) {
          mappedWorkfields.push(name);
        }
      });
    }

    if (mappedWorkfields.length > 0) {
      metadata.workfields = mappedWorkfields;
    }

    metadata.theme = sourceNode.getIn(['field_theme', 0, 'entity', 'name', 0, 'value'], null);

    // Machine value (`verkennen`, `verdiepen`, …) rather than the label, so the
    // GA4 dimension stays stable if the editorial labels change.
    metadata.customerJourneyPhase = sourceNode.getIn(['field_customer_journey_phase', 0, 'value'], null);

    return metadata;
  };

  // Build taxonomy metadata in format { theme, subjects, workfields }
  const buildTaxonomyMetadata = (sourceTerm) => {
    return {
      subjects: sourceTerm?.getIn(['metadata', 'subjects'])?.toJS() || [],
      theme: sourceTerm?.getIn(['metadata', 'theme']),
      workfields: sourceTerm?.getIn(['metadata', 'workfields'])?.toJS() || [],
    };
  };

  const currentEntity = useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPES.NODE:
        if (node) {
          let newNode = node.set('pageTitle', node.getIn(['title', 0, 'value']));

          const metadata = buildNodeMetadata(newNode);
          newNode = newNode.set('metadata', metadata);

          return newNode;
        }
        break;
      case ENTITY_TYPES.TAXONOMY_TERM:
        if (taxonomyTerm) {
          let newTaxonomyTerm = taxonomyTerm.set('pageTitle', taxonomyTerm.getIn(['name', 0, 'value']));

          const metadata = buildTaxonomyMetadata(newTaxonomyTerm);
          newTaxonomyTerm = newTaxonomyTerm.set('metadata', metadata);

          return newTaxonomyTerm;
        }
        break;
      case ENTITY_TYPES.WEBFORM:
        if (webform) {
          return webform.set('pageTitle', webform.getIn(['info', 'title']));
        }
        break;
      default:
        return null;
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType, node, webform, taxonomyTerm]);

  useEffect(() => {
    if (currentEntity?.get('pageTitle')) {
      const pageTitle = currentEntity.getIn(['pageTitle'], '');

      dispatch(clearPageInformation());
      dispatch(setPageInformation(`${pageTitle} | VeiligheidNL`, {
        subjects: currentEntity.getIn(['metadata', 'subjects'], null),
        workfields: currentEntity.getIn(['metadata', 'workfields'], null),
        theme: currentEntity.getIn(['metadata', 'theme'], null),
        customerJourneyPhase: currentEntity.getIn(['metadata', 'customerJourneyPhase'], null),
      }));
      dispatch(setPageViewSent(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntity]);

  useEffect(() => {
    if (entityNotLoaded || (!entityNotLoaded && pathIsDifferentAndHasEntity)) {
      dispatch(referenceLoadingReset());
      dispatch(entityGetByPath(path));
    }
  }, [dispatch, entityNotLoaded, pathIsDifferentAndHasEntity, path]);

  const renderPage = () => {
    if (entityStatus === 404) {
      return (
        <Container>
          <NotFound />
        </Container>
      );
    } else if (entityStatus === 403) {
      return (
        <Container>
          <ServerError />
        </Container>
      );
    } else if (entityStatus === 500) {
      return (
        <Container>
          <ServerError />
        </Container>
      );
    }

    // Block direct access to FAQ detail pages
    // FAQ content is designed to be used within paragraphs, not as standalone pages
    if (entityType === ENTITY_TYPES.NODE && node) {
      const nodeType = node.getIn(['type', 0, 'target_id']);
      if (nodeType === 'faq') {
        return (
          <Container>
            <NotFound />
          </Container>
        );
      }
    }

    return (
      <Page title={childEntity.getIn(['title', 0, 'value'], '')}>
        {entityType === ENTITY_TYPES.NODE && (
          <Node root id={entityId} />
        )}
        {entityType === ENTITY_TYPES.TAXONOMY_TERM && (
          <TaxonomyTerm root id={entityId} />
        )}
        {entityType === ENTITY_TYPES.WEBFORM && (
          <WebformWrapper root id={entityId} wrapInContainer />
        )}
      </Page>
    );
  };

  if ((!entityType || !entityId) && entityExists) {
    return null;
  }

  return (
    <div>
      {renderPage()}
    </div>
  );
};

export default Entity;
